import React, { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  LinearProgress,
  Grid,
  TablePagination,
} from '@mui/material';
import {
  Add as AddIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
  Tag as TagIcon,
  Style as StyleIcon,
  Palette as PaletteIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useStore } from 'react-admin';
import type { ColorPropertiesStats, ColorPropertiesFilter, Partner, ColorProperty } from '@src/types';
import * as RewareApi from '@src/apis/reware';
import { ColorPropertiesTable } from './ColorPropertiesTable';
import { ImportFacetMappingsDialog } from './ImportFacetMappingsDialog';
import { ImportConfirmationDialog } from './ImportConfirmationDialog';
import { generateColorPropertiesCSV, downloadCSV } from './exportUtils';
import {
  parseColorPropertiesCSV,
  calculateColorImportStats,
  importColorProperties,
  type ParsedColorProperty,
  type ImportStats,
} from './importUtils';

export const ColorPropertiesList: React.FC = () => {
  const navigate = useNavigate();
  const [currentPartner] = useStore<Partner | null>('currentPartner', null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [colorGroup, setColorGroup] = useState('all');
  const [status, setStatus] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [total, setTotal] = useState(0);
  const [colorProperties, setColorProperties] = useState<ColorProperty[]>([]);
  const [stats, setStats] = useState<ColorPropertiesStats>({
    total: 0,
    missingHex: 0,
    missingSwatch: 0,
    missingGroup: 0,
    completeness: 0,
  });
  const [colorGroups, setColorGroups] = useState<string[]>([]);
  const [loadError, setLoadError] = useState<string>('');
  const [statsError, setStatsError] = useState<string>('');

  // Import state
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [importStats, setImportStats] = useState<ImportStats | null>(null);
  const [parsedImport, setParsedImport] = useState<ParsedColorProperty[] | null>(null);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);

  // Reset to page 0 when filters change
  useEffect(() => {
    setPage(0);
  }, [searchQuery, colorGroup, status]);

  useEffect(() => {
    loadData();
  }, [searchQuery, colorGroup, status, currentPartner, page, rowsPerPage]);

  // Mapping completeness reflects every code for the partner, so it's loaded
  // separately from the filtered/paginated table data and only refreshes when
  // the partner changes — not on every search keystroke or page change.
  useEffect(() => {
    loadStats();
  }, [currentPartner]);

  // Helper to fetch all color properties across all pages
  const fetchAllColorProperties = async (): Promise<ColorProperty[]> => {
    if (!currentPartner) return [];

    const allProperties: ColorProperty[] = [];
    let currentPage = 1;
    let totalPages = 1;

    do {
      const response = await RewareApi.getColorProperties({
        partnerCode: currentPartner.code,
        filter: {},
        page: currentPage,
        perPage: 200,
      });

      allProperties.push(...response.data);
      totalPages = Math.ceil(response.total / 200);
      currentPage++;
    } while (currentPage <= totalPages);

    return allProperties;
  };

  const loadData = async () => {
    if (!currentPartner) {
      console.warn('No partner selected');
      setLoading(false);
      return;
    }

    setLoading(true);
    setLoadError('');
    try {
      const filter: ColorPropertiesFilter = {
        searchQuery,
        colorGroup: colorGroup !== 'all' ? colorGroup : undefined,
        status: status !== 'all' ? (status as 'complete' | 'incomplete') : undefined,
      };

      // If colorGroup or status filters are active, we need to fetch all data
      // to do client-side filtering since the API doesn't support these filters
      const hasClientSideFilters = filter.colorGroup || filter.status;

      if (hasClientSideFilters) {
        // Fetch all data for client-side filtering
        const allData = await fetchAllColorProperties();

        // Apply client-side filters
        let filtered = allData;

        if (filter.searchQuery) {
          const searchLower = filter.searchQuery.toLowerCase();
          filtered = filtered.filter(p =>
            p.code.toLowerCase().includes(searchLower) ||
            p.display.toLowerCase().includes(searchLower)
          );
        }

        if (filter.colorGroup) {
          filtered = filtered.filter(p =>
            p.family && p.family.includes(filter.colorGroup!)
          );
        }

        if (filter.status === 'complete') {
          filtered = filtered.filter(p =>
            p.hex_code && p.family && p.family.length > 0
          );
        } else if (filter.status === 'incomplete') {
          filtered = filtered.filter(p =>
            !p.hex_code || !p.family || p.family.length === 0
          );
        }

        // Apply client-side pagination
        const startIndex = page * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;
        const paginatedData = filtered.slice(startIndex, endIndex);

        setColorProperties(paginatedData);
        setTotal(filtered.length);
      } else {
        // Use server-side pagination when no client-side filters are needed
        const response = await RewareApi.getColorProperties({
          partnerCode: currentPartner.code,
          filter,
          page: page + 1, // API uses 1-based indexing
          perPage: rowsPerPage,
        });

        setColorProperties(response.data);
        setTotal(response.total);
      }
    } catch (error) {
      console.error('Failed to load color properties:', error);
      setColorProperties([]);
      setTotal(0);
      setLoadError('Couldn’t load color codes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    if (!currentPartner) {
      return;
    }

    try {
      // There's no dedicated aggregate endpoint for these code-mapping
      // tables, so stats and the group list are computed client-side over
      // every page for the partner.
      const allProperties = await fetchAllColorProperties();

      setStats(RewareApi.calculateColorPropertiesStats(allProperties));

      // Extract unique color groups from all properties
      const groupsSet = new Set<string>();
      allProperties.forEach((property) => {
        property.family?.forEach((group) => {
          if (group && group.trim() !== '') {
            groupsSet.add(group);
          }
        });
      });
      const sortedGroups = Array.from(groupsSet).sort((a, b) => a.localeCompare(b));
      setColorGroups(sortedGroups);
      setStatsError('');
    } catch (error) {
      console.error('Failed to load color property stats:', error);
      setStatsError('Couldn’t load completeness metrics.');
    }
  };

  const handleAddColorCode = () => {
    navigate('/catalog/attributes/colors/add');
  };

  const handleImport = () => {
    setImportDialogOpen(true);
  };

  const handleImportFileSelected = async (file: File) => {
    if (!currentPartner) return;

    try {
      const content = await file.text();
      const parsed = parseColorPropertiesCSV(content);

      // Fetch all existing properties to calculate diff
      const existingProperties = await fetchAllColorProperties();

      const stats = calculateColorImportStats(parsed, existingProperties);

      setImportStats(stats);
      setParsedImport(parsed);
      setImportDialogOpen(false);
      setConfirmDialogOpen(true);
    } catch (error) {
      console.error('Import parsing failed:', error);
      alert('Failed to parse import file. Please check the file format.');
      setImportDialogOpen(false);
    }
  };

  const handleConfirmImport = async () => {
    if (!parsedImport || !currentPartner || !importStats) return;

    try {
      setImporting(true);

      // Get API token from react-admin store
      const token = localStorage.getItem('token') || '';

      // Fetch all existing properties to ensure we have latest data
      const existingProperties = await fetchAllColorProperties();

      const result = await importColorProperties(
        currentPartner.code,
        token,
        parsedImport,
        existingProperties,
        setImportProgress
      );

      setConfirmDialogOpen(false);
      setImporting(false);
      setImportProgress(0);

      if (result.success) {
        alert(`Import successful! Added: ${result.added}, Updated: ${result.updated}`);
      } else {
        alert(`Import completed with errors. Added: ${result.added}, Updated: ${result.updated}, Failed: ${result.failed}\n\nErrors:\n${result.errors.slice(0, 5).join('\n')}`);
      }

      // Reload data
      void loadData();
      void loadStats();
    } catch (error) {
      console.error('Import failed:', error);
      alert('Import failed. Please try again.');
      setImporting(false);
      setImportProgress(0);
    }
  };

  const handleExport = async () => {
    if (!currentPartner) return;

    try {
      setLoading(true);

      // Fetch all properties across all pages
      const allProperties = await fetchAllColorProperties();

      const csv = generateColorPropertiesCSV(allProperties);
      const timestamp = new Date().toISOString().split('T')[0];
      downloadCSV(csv, `color-mappings-${currentPartner.code}-${timestamp}.csv`);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event: React.MouseEvent | null, newPage: number) => {
    event?.preventDefault();
    event?.stopPropagation();

    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when changing rows per page
  };

  return (
    <Box>
      {/* Stats Cards */}
      <Card sx={{ mb: 3, backgroundColor: '#f5f5f5' }}>
        <CardContent>
          {statsError ? (
            <Alert severity="error">{statsError}</Alert>
          ) : (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2">Mapping completeness</Typography>
                <Typography variant="subtitle2">
                  {stats.completeness}
                  {'% of '}
                  {stats.total}
                  {' codes'}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={stats.completeness}
                sx={{ height: 8, borderRadius: 4, mb: 2 }}
              />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Card variant="outlined">
                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <TagIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {stats.missingHex}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Missing hex
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card variant="outlined">
                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <StyleIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {stats.missingSwatch}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Missing swatch
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card variant="outlined">
                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <PaletteIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {stats.missingGroup}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Missing color groups
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </>
          )}
        </CardContent>
      </Card>

      {/* Filters and Actions */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search code or name"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
          }}
          sx={{ flex: 1, minWidth: 250 }}
        />
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Color Group</InputLabel>
          <Select
            value={colorGroup}
            label="Color Group"
            onChange={(e) => setColorGroup(e.target.value)}
          >
            <MenuItem value="all">All groups</MenuItem>
            {colorGroups.map(group => (
              <MenuItem key={group} value={group}>
                {group}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={status}
            label="Status"
            onChange={(e) => setStatus(e.target.value)}
          >
            <MenuItem value="all">All statuses</MenuItem>
            <MenuItem value="complete">Complete</MenuItem>
            <MenuItem value="incomplete">Incomplete</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Button
          startIcon={<UploadIcon />}
          onClick={handleImport}
          variant="outlined"
        >
          Import
        </Button>
        <Button
          startIcon={<DownloadIcon />}
          onClick={handleExport}
          variant="outlined"
        >
          Export
        </Button>
        <Button
          startIcon={<AddIcon />}
          onClick={handleAddColorCode}
          variant="contained"
          sx={{ ml: 'auto' }}
        >
          Add color code
        </Button>
      </Box>

      {/* Data List */}
      {loading && colorProperties.length === 0 ? (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <Typography>Loading...</Typography>
            </Box>
          </CardContent>
        </Card>
      ) : loadError ? (
        <Card>
          <CardContent>
            <Alert severity="error">{loadError}</Alert>
          </CardContent>
        </Card>
      ) : colorProperties.length === 0 ? (
        <Card>
          <CardContent>
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="body1" color="text.secondary">
                No color codes match the current filters.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      ) : (
        // Keep the table mounted and just dim it while a page/filter change is
        // in flight, instead of swapping it out for a loading state. Unmounting
        // it collapses the page height, which yanks the scrollbar to the top
        // for the duration of the fetch and snaps back once data returns.
        <Box sx={{ position: 'relative', opacity: loading ? 0.6 : 1, transition: 'opacity 0.15s ease' }}>
          {loading && (
            <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 1 }} />
          )}
          <ColorPropertiesTable data={colorProperties} />
          <Card>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              disabled={loading}
            />
          </Card>
        </Box>
      )}

      <ImportFacetMappingsDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        onFileSelected={handleImportFileSelected}
        variant="color"
      />

      {importStats && (
        <ImportConfirmationDialog
          open={confirmDialogOpen}
          onClose={() => setConfirmDialogOpen(false)}
          onConfirm={handleConfirmImport}
          stats={importStats}
          variant="color"
          loading={importing}
          progress={importProgress}
        />
      )}
    </Box>
  );
};
